/**
 * View Status Helpers
 *
 * Pure utility functions for building view status maps and checking
 * reflection "new" status. Extracted from App.js where these patterns
 * were duplicated 6+ times.
 */

/**
 * Build a view status map from an array of reflections.
 * Each reflection object from the API contains embedded view status fields
 * (is_viewed, badge_dismissed, etc.) from the LEFT JOIN with reflection_view_status.
 *
 * @param {Array} reflections - Array of reflection objects from the API
 * @returns {Object} Map of reflectionId -> viewStatus object
 */
export function buildViewStatusMap(reflections) {
  const viewStatusMap = {};
  reflections.forEach(reflection => {
    viewStatusMap[reflection.id] = {
      reflection_id: reflection.id,
      is_viewed: reflection.is_viewed || false,
      badge_dismissed: reflection.badge_dismissed || false,
      studio_opened: reflection.studio_opened || false,
      studio_opened_at: reflection.studio_opened_at,
      last_viewed_at: reflection.last_viewed_at,
      video_played: reflection.video_played,
      audio_played: reflection.audio_played,
      text_expanded: reflection.text_expanded
    };
  });
  return viewStatusMap;
}

/**
 * Build a view status map from a batch response (grouped by prompt_id).
 *
 * @param {Object} reflectionsByPrompt - Object mapping promptId -> reflection[]
 * @returns {Object} Map of reflectionId -> viewStatus object
 */
export function buildViewStatusMapFromBatch(reflectionsByPrompt) {
  const viewStatusMap = {};
  Object.values(reflectionsByPrompt).forEach(reflections => {
    reflections.forEach(reflection => {
      viewStatusMap[reflection.id] = {
        reflection_id: reflection.id,
        is_viewed: reflection.is_viewed || false,
        badge_dismissed: reflection.badge_dismissed || false,
        studio_opened: reflection.studio_opened || false,
        studio_opened_at: reflection.studio_opened_at,
        last_viewed_at: reflection.last_viewed_at,
        video_played: reflection.video_played,
        audio_played: reflection.audio_played,
        text_expanded: reflection.text_expanded
      };
    });
  });
  return viewStatusMap;
}

/**
 * Determine if a reflection should show the "NEW" badge.
 *
 * @param {Object} reflection - The reflection object
 * @param {Object} viewStatuses - Map of reflectionId -> viewStatus
 * @param {number|null} currentUserId - The current user's ID
 * @returns {boolean} Whether the reflection should show a NEW badge
 */
export function isReflectionNew(reflection, viewStatuses, currentUserId) {
  // Only show badge for shared reflections FROM others
  if (reflection.user_id === currentUserId) return false;

  const viewStatus = viewStatuses[reflection.id];

  // If manually dismissed, never show
  if (viewStatus?.badge_dismissed) return false;

  // "Seen" = opened Studio for this reflection
  if (!viewStatus?.studio_opened) return true;

  // If reflection updated after last Studio open, it's new again
  if (viewStatus.studio_opened_at) {
    return new Date(reflection.updated_at) > new Date(viewStatus.studio_opened_at);
  }

  return false;
}

/**
 * Check if a reflection matches the current date filter.
 *
 * @param {Object} reflection - The reflection object (needs updated_at field)
 * @param {Object} dateFilter - Date filter configuration
 * @param {string} dateFilter.type - 'none', 'last-week', 'last-month', 'since', 'between'
 * @param {string} [dateFilter.since] - ISO date string for 'since' filter
 * @param {string} [dateFilter.from] - ISO date string for 'between' start
 * @param {string} [dateFilter.to] - ISO date string for 'between' end
 * @returns {boolean} Whether the reflection matches the date filter
 */
export function matchesDateFilter(reflection, dateFilter) {
  if (!dateFilter || dateFilter.type === 'none') return true;

  if (!reflection.updated_at) {
    return true; // Show reflections without updated_at
  }

  const updatedAt = new Date(reflection.updated_at);
  if (isNaN(updatedAt.getTime())) {
    return true; // Show reflections with invalid dates
  }

  const now = new Date();

  switch (dateFilter.type) {
    case 'last-week': {
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return updatedAt >= oneWeekAgo;
    }
    case 'last-month': {
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return updatedAt >= oneMonthAgo;
    }
    case 'since': {
      if (!dateFilter.since) return true;
      const sinceDate = new Date(dateFilter.since);
      if (isNaN(sinceDate.getTime())) return true;
      return updatedAt >= sinceDate;
    }
    case 'between': {
      if (!dateFilter.from || !dateFilter.to) return true;
      const fromDate = new Date(dateFilter.from);
      const toDate = new Date(dateFilter.to);
      if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return true;
      return updatedAt >= fromDate && updatedAt <= toDate;
    }
    default:
      return true;
  }
}

/**
 * Parse search text for special patterns like user:xxx, title:xxx, text:xxx
 * Follows common industry patterns (GitHub, Jira, etc.)
 *
 * Supported patterns:
 *   - user:username - Filter by username
 *   - title:xxx - Filter by reflection title only
 *   - text:xxx - Filter by text content, transcriptions, and image names
 *   - Quoted values supported: title:"multi word", text:'multi word'
 *   - Regular text - Full-text search across all fields
 *
 * @param {string} searchText - Raw search text
 * @returns {Object} { userFilter, titleFilter, textFilter, textSearch }
 */
export function parseSearchText(searchText) {
  if (!searchText || !searchText.trim()) {
    return { userFilter: null, titleFilter: null, textFilter: null, textSearch: '' };
  }

  let remaining = searchText.trim();

  // Helper to extract a pattern and remove it from remaining text
  const extractPattern = (prefix) => {
    const pattern = new RegExp(`${prefix}:(?:"([^"]+)"|'([^']+)'|(\\S+))`, 'i');
    const match = remaining.match(pattern);
    if (match) {
      const value = (match[1] || match[2] || match[3]).toLowerCase();
      remaining = remaining.replace(pattern, '').trim();
      return value;
    }
    return null;
  };

  const userFilter = extractPattern('user');
  const titleFilter = extractPattern('title');
  const textFilter = extractPattern('text');

  return {
    userFilter,
    titleFilter,
    textFilter,
    textSearch: remaining.toLowerCase()
  };
}

/**
 * Check if search text has any special filter pattern
 * @param {string} searchText - Raw search text
 * @returns {boolean} True if contains user:, title:, or text: pattern
 */
export function hasUserFilter(searchText) {
  if (!searchText || !searchText.trim()) return false;
  return /(?:user|title|text):/i.test(searchText);
}

/**
 * Filter reflections based on search text, checking text_content, title, created_by,
 * transcription text (both video and audio), and image titles.
 *
 * Supports special patterns (AND-combined):
 *   - user:username - Filter by username
 *   - title:xxx - Filter by title only
 *   - text:xxx - Filter by text content, transcriptions, image titles
 *   - Regular text - Full-text search across all fields
 *
 * @param {Object} reflection - The reflection object
 * @param {string} searchText - Search query (may contain special patterns)
 * @returns {boolean} Whether the reflection matches the search
 */
export function matchesSearch(reflection, searchText) {
  if (!searchText || !searchText.trim()) return true;

  const { userFilter, titleFilter, textFilter, textSearch } = parseSearchText(searchText);
  const createdBy = reflection.created_by?.toLowerCase() || '';

  // Check user filter
  if (userFilter && !createdBy.includes(userFilter)) return false;

  // Check title filter — only searches title field
  if (titleFilter) {
    const title = reflection.title?.toLowerCase() || '';
    if (!title.includes(titleFilter)) return false;
  }

  // Check text filter — searches text_content, transcriptions, image titles
  if (textFilter) {
    const textContent = reflection.text_content?.toLowerCase() || '';
    const videoTx = reflection.video_transcription_text?.toLowerCase() || '';
    const audioTx = reflection.audio_transcription_text?.toLowerCase() || '';
    const imageNames = (reflection.images || []).map(img => (img.title || '').toLowerCase()).join(' ');
    if (!textContent.includes(textFilter) && !videoTx.includes(textFilter) &&
        !audioTx.includes(textFilter) && !imageNames.includes(textFilter)) return false;
  }

  // If only special filters (no general text), done
  if (!textSearch) return true;

  // General text search (existing behavior: all fields)
  const reflectionText = reflection.text_content?.toLowerCase() || '';
  const title = reflection.title?.toLowerCase() || '';
  const videoTranscription = reflection.video_transcription_text?.toLowerCase() || '';
  const audioTranscription = reflection.audio_transcription_text?.toLowerCase() || '';

  return reflectionText.includes(textSearch) ||
         title.includes(textSearch) ||
         createdBy.includes(textSearch) ||
         videoTranscription.includes(textSearch) ||
         audioTranscription.includes(textSearch);
}

/**
 * Check if a prompt text matches the search criteria.
 * The prompt text is treated as the prompt's "title", so title: filters apply to it.
 * General text search also matches against prompt text.
 * user: and text: filters do not apply to prompts (those are reflection-level).
 *
 * @param {string} promptText - The prompt text
 * @param {string} searchText - Search query (may contain special patterns)
 * @returns {boolean} Whether the prompt matches the search
 */
export function promptMatchesSearch(promptText, searchText) {
  if (!searchText || !searchText.trim()) return true;

  const { titleFilter, textSearch } = parseSearchText(searchText);
  const prompt = promptText?.toLowerCase() || '';

  // title: filter applies to prompt text
  if (titleFilter && prompt.includes(titleFilter)) return true;

  // General text search applies to prompt text
  if (textSearch && prompt.includes(textSearch)) return true;

  // If neither titleFilter nor textSearch is set, prompt can't match on its own
  // (user: and text: are reflection-level only)
  if (!titleFilter && !textSearch) return false;

  return false;
}
